/** OnboardCastingType — the pre-profile picker that sits BEFORE the
 *  L11 onboarding form.
 *
 *  Per user's spec (Feb 2026):
 *    · L11 covers TWO distinct businesses that look similar but work
 *      very differently:
 *        (a) Casting Agency — cast talent FOR a role, paid per-project
 *            by producers. Ghost examples: Mukesh Chhabra Casting,
 *            Nandini Shrikent, Casting Bay.
 *        (b) Talent Management Agency — represent talent long-term,
 *            paid via commission on their deals. Ghost examples:
 *            KWAN, YRF Talent, Matrix, Collective Artists, Bling.
 *    · Picker mirrors the /onboard/studio (OnboardStudioType) pattern.
 *
 *  Selection flows to /onboard/l10-details with the picked kind in
 *  route state. OnboardGeneric handles the rest of the wizard.
 */
import { useNavigate } from "react-router-dom";
import OnboardBackButton from "@/components/OnboardBackButton";
import { ArrowRight, Users, UserSquare2, Sparkles, Briefcase } from "lucide-react";

const OPTIONS = [
  {
    kind: "casting_agency",
    label: "Casting Agency",
    accent: "#A78BFA",
    Icon: UserSquare2,
    tagline: "You cast talent for productions.",
    bullets: [
      "Producer briefs a role — you find the actor.",
      "Paid per-project by production houses / ad agencies.",
      "Run breakdowns, auditions, callbacks, chemistry reads.",
      "Broad open database — you don't exclusively rep the talent.",
    ],
    ghost: "Like Mukesh Chhabra Casting · Nandini Shrikent · Casting Bay · Roshan Talkies",
  },
  {
    kind: "talent_management_agency",
    label: "Talent Management Agency",
    accent: "#22D3EE",
    Icon: Sparkles,
    tagline: "You represent talent long-term.",
    bullets: [
      "Curated exclusive roster on a signed contract.",
      "Paid via % commission on the talent's earnings.",
      "Pitch your roster to producers, brands, event promoters.",
      "Handle brand endorsements & appearances beyond film work.",
    ],
    ghost: "Like KWAN · YRF Talent · Matrix · Collective Artists · Bling",
  },
];

export default function OnboardCastingType() {
  const nav = useNavigate();

  const pick = (kind) => {
    nav("/onboard/l10-details", { state: { kind } });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14"
         data-testid="onboard-l10-type-page">
      <OnboardBackButton fallback="/onboard" testId="onboard-l10-type-back" />

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          L11 · Sign up · Step 1 of 2
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
          Which one are you?
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Both represent actors — but they run very different businesses. Pick
          the one that matches how you make money and how your talent
          relationship works. You can add the other arm later from Settings.
        </p>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-5" data-testid="l10-picker-grid">
        {OPTIONS.map((o) => {
          const Icon = o.Icon;
          return (
            <button
              key={o.kind}
              type="button"
              onClick={() => pick(o.kind)}
              className="cs-card text-left rounded-2xl p-7 relative overflow-hidden group hover:border-white/30 transition-all"
              style={{ borderColor: `${o.accent}33` }}
              data-testid={`l10-picker-card-${o.kind}`}>
              <div
                className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-10 blur-3xl pointer-events-none transition-opacity group-hover:opacity-25"
                style={{ background: o.accent }}
              />
              <div className="flex items-start gap-4 relative">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: `${o.accent}26`, color: o.accent }}
                >
                  <Icon size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-2xl uppercase tracking-tight">
                    {o.label}
                  </h2>
                  <p className="mt-1.5 text-sm text-white/75">{o.tagline}</p>

                  <ul className="mt-4 space-y-1.5">
                    {o.bullets.map((b, i) => (
                      <li key={i} className="text-[12px] text-white/65 leading-relaxed flex gap-2">
                        <span className="text-white/30 shrink-0">·</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 pt-4 border-t border-white/8 flex items-start gap-2"
                       data-testid={`l10-picker-ghost-${o.kind}`}>
                    <Briefcase size={12} className="text-white/40 mt-0.5 shrink-0" />
                    <p className="text-[11px] text-white/50 italic leading-relaxed">
                      {o.ghost}
                    </p>
                  </div>

                  <div
                    className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-semibold transition-opacity group-hover:opacity-80"
                    style={{ color: o.accent }}
                  >
                    Continue as {o.label} <ArrowRight size={13} />
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-10 cs-card rounded-2xl p-5 flex items-start gap-4"
           data-testid="l10-picker-hybrid-note">
        <Users size={16} className="text-emerald-400 shrink-0 mt-0.5" />
        <p className="text-[12px] text-white/70 leading-relaxed">
          Running <span className="text-white/90 font-semibold">both</span> under
          one shop (like KWAN&apos;s casting arm)? Pick the one that generates most
          of your revenue today — you can enable the other cubicle from
          Settings once your primary profile is set up.
        </p>
      </div>
    </div>
  );
}
